/**
 * Memoria locale dei lavori recenti.
 *
 * I documenti pronti vivono nella memoria della pagina; il motore li tiene
 * circa un'ora. Un aggiornamento della pagina, prima, buttava via tutto:
 * anteprime, e dopo il pagamento anche l'identificativo della sessione
 * Stripe che sblocca i documenti. Qui si ricorda il minimo che basta a
 * ricostruire la pagina finche' il motore ha ancora i lavori: gli id dei
 * lavori, la sessione pagata se c'e', quando e' finita l'elaborazione e i
 * nomi dei file, per riconoscerli nell'elenco.
 *
 * Due chiavi. Il LAVORO CORRENTE e' quello che la pagina ricostruisce da sola
 * al caricamento. La CRONOLOGIA tiene le ultime anonimizzazioni fatte da
 * questo browser, finche' il motore le ha (l'elenco "lavori recenti" di chi
 * non ha un abbonamento: chi ce l'ha lo legge dal backend, per tutto il
 * team). Ogni salvataggio aggiorna entrambe.
 *
 * Nel browser non finisce nessun documento: identificativi e nomi di file.
 * Funzioni pure, con lo storage iniettabile per le prove; con lo storage
 * vero ogni cambiamento avvisa la pagina con un evento, perche' lo
 * storage non lo fa nella stessa scheda.
 */

export const CHIAVE_LAVORO = "obscura_lavoro_corrente";
export const CHIAVE_CRONOLOGIA = "obscura_lavori_recenti";
/** Un po' meno dell'ora del motore, per non promettere quello che e' gia' sparito. */
export const DURATA_LAVORO_MS = 55 * 60 * 1000;
export const MAX_CRONOLOGIA = 12;
export const EVENTO_LAVORI = "obscura:lavori";

function storage(s) {
  if (s) return s;
  try { return window.localStorage; } catch (_) { return null; }
}

function pulisci(ids) {
  return (Array.isArray(ids) ? ids : [])
    .filter((id) => typeof id === "string" && id.trim())
    .map((id) => id.trim());
}

function pulisciNomi(nomi) {
  return (Array.isArray(nomi) ? nomi : [])
    .filter((n) => typeof n === "string" && n.trim())
    .map((n) => n.trim().slice(0, 200));
}

/** Gli stessi lavori, in qualunque ordine, sono la stessa voce. */
export function chiaveLavori(jobIds) {
  return [...pulisci(jobIds)].sort().join("|");
}

function avvisa(s) {
  if (s) return; // storage di prova: nessuna pagina in ascolto
  try { window.dispatchEvent(new CustomEvent(EVENTO_LAVORI)); } catch (_) { /* senza finestra */ }
}

function normalizza(dati) {
  const ids = pulisci(dati?.jobIds);
  if (!ids.length || !Number.isFinite(dati?.ts)) return null;
  return {
    jobIds: ids,
    sessionId: typeof dati.sessionId === "string" && dati.sessionId ? dati.sessionId : null,
    ts: dati.ts,
    nomi: pulisciNomi(dati.nomi),
    // Inviato al motore e non ancora pronto: la barra laterale lo segue.
    ...(dati.inCorso === true ? { inCorso: true } : {}),
  };
}

function leggiTutti(s) {
  try {
    const raw = storage(s)?.getItem(CHIAVE_CRONOLOGIA);
    const lista = raw ? JSON.parse(raw) : [];
    return (Array.isArray(lista) ? lista : []).map(normalizza).filter(Boolean);
  } catch (_) {
    return [];
  }
}

function scriviTutti(lista, s) {
  try { storage(s)?.setItem(CHIAVE_CRONOLOGIA, JSON.stringify(lista)); } catch (_) { /* niente storage */ }
}

/**
 * Ricorda il lavoro corrente e lo mette (o aggiorna) in cima alla cronologia.
 * Senza nomi si tengono quelli gia' noti per gli stessi lavori.
 * @returns {{jobIds: string[], sessionId: string|null, ts: number, nomi: string[]}|null}
 */
export function salvaLavoro({ jobIds, sessionId = null, ts = Date.now(), nomi, inCorso = false }, s, adesso = ts) {
  const ids = pulisci(jobIds);
  if (!ids.length) return null;
  const chiave = chiaveLavori(ids);
  // Le voci scadute escono qui: i nomi dei file non restano nel browser
  // oltre la vita dei documenti (e oltre quello che dice la cookie policy).
  const tutti = leggiTutti(s).filter((v) => adesso - v.ts <= DURATA_LAVORO_MS);
  const precedente = tutti.find((v) => chiaveLavori(v.jobIds) === chiave);
  const nomiPuliti = pulisciNomi(nomi);
  const lavoro = {
    jobIds: ids,
    sessionId: typeof sessionId === "string" && sessionId ? sessionId : null,
    ts,
    nomi: nomiPuliti.length ? nomiPuliti : precedente?.nomi || [],
    ...(inCorso ? { inCorso: true } : {}),
  };
  try { storage(s)?.setItem(CHIAVE_LAVORO, JSON.stringify(lavoro)); } catch (_) { /* niente storage */ }
  scriviTutti([lavoro, ...tutti.filter((v) => chiaveLavori(v.jobIds) !== chiave)].slice(0, MAX_CRONOLOGIA), s);
  avvisa(s);
  return lavoro;
}

function leggiCorrente(s) {
  try {
    const raw = storage(s)?.getItem(CHIAVE_LAVORO);
    return raw ? normalizza(JSON.parse(raw)) : null;
  } catch (_) {
    return null;
  }
}

/** Il lavoro corrente, se ancora dentro la finestra del motore. */
export function leggiLavoro(s, adesso = Date.now()) {
  const lavoro = leggiCorrente(s);
  if (!lavoro) return null;
  if (adesso - lavoro.ts > DURATA_LAVORO_MS) {
    try { storage(s)?.removeItem(CHIAVE_LAVORO); } catch (_) { /* noop */ }
    return null;
  }
  return lavoro;
}

/** Le anonimizzazioni recenti di questo browser, dalla piu' nuova, ancora vive nel motore. */
export function leggiCronologia(s, adesso = Date.now()) {
  const tutti = leggiTutti(s);
  const vive = tutti.filter((v) => adesso - v.ts <= DURATA_LAVORO_MS);
  // Quello che e' scaduto si cancella anche dalla memoria, non solo dalla vista.
  if (vive.length !== tutti.length) scriviTutti(vive, s);
  return vive.sort((a, b) => b.ts - a.ts);
}

/** Dopo un pagamento riuscito: stessa memoria, con la sessione che sblocca. */
export function aggiornaSessioneLavoro(sessionId, s, adesso = Date.now()) {
  const lavoro = leggiLavoro(s, adesso);
  if (!lavoro || !sessionId) return lavoro;
  return salvaLavoro({ ...lavoro, sessionId }, s);
}

/** Il lavoro corrente non e' piu' quello aperto; la cronologia resta. */
export function dimenticaLavoro(s) {
  try { storage(s)?.removeItem(CHIAVE_LAVORO); } catch (_) { /* noop */ }
  avvisa(s);
}

/**
 * Un lavoro "in corso" e' concluso e la pagina non lo stava guardando (la
 * barra laterale ne segue lo stato): non e' piu' in corso, e la sua ora
 * diventa quella della fine, da cui il motore conta l'ora di vita. Il lavoro
 * corrente, se e' lui, cambia uguale.
 */
export function concludiInCronologia(jobIds, ts, s) {
  const chiave = chiaveLavori(jobIds);
  if (!chiave || !Number.isFinite(ts)) return;
  const tutti = leggiTutti(s);
  if (!tutti.some((v) => chiaveLavori(v.jobIds) === chiave)) return;
  const concluso = (v) => {
    const { inCorso: _concluso, ...resto } = v;
    return { ...resto, ts };
  };
  scriviTutti(tutti.map((v) => (chiaveLavori(v.jobIds) === chiave ? concluso(v) : v)), s);
  const corrente = leggiCorrente(s);
  if (corrente && chiaveLavori(corrente.jobIds) === chiave) {
    try { storage(s)?.setItem(CHIAVE_LAVORO, JSON.stringify(concluso(corrente))); } catch (_) { /* niente storage */ }
  }
  avvisa(s);
}

const CONCLUSI_MALE = ["failed", "error", "cancelled", "timeout"];

/**
 * Cosa dicono gli stati dei lavori di una voce, come li da'
 * /api/jobs/{id}/stato ("sparito" per un 404, null se non si e' saputo):
 * "sparito" se il motore non ne ha piu' uno, "pronto" se sono tutti conclusi
 * e almeno uno e' riuscito ("Riapri" salta gli altri), "fallito" se nessuno
 * e' riuscito, "in_corso" altrimenti. null se uno stato manca: si riprova al
 * giro dopo.
 */
export function esitoStati(stati) {
  const lista = Array.from(stati || []);
  if (!lista.length || lista.some((x) => x == null)) return null;
  if (lista.includes("sparito")) return "sparito";
  const conclusi = lista.every((x) => x === "completed" || CONCLUSI_MALE.includes(String(x).toLowerCase()));
  if (!conclusi) return "in_corso";
  return lista.includes("completed") ? "pronto" : "fallito";
}

/** L'ora di un aggiornamento del motore (ISO in UTC, senza fuso) in millisecondi; null se non si legge. */
export function oraDalMotore(testo) {
  if (typeof testo !== "string" || !testo.trim()) return null;
  const conFuso = /(Z|[+-]\d\d:?\d\d)$/i.test(testo.trim()) ? testo.trim() : `${testo.trim()}Z`;
  const ms = Date.parse(conFuso);
  return Number.isFinite(ms) ? ms : null;
}

/** Il motore non ha piu' questi lavori: via dall'elenco, e dal corrente se era lui. */
export function dimenticaDallaCronologia(jobIds, s) {
  const chiave = chiaveLavori(jobIds);
  if (!chiave) return;
  scriviTutti(leggiTutti(s).filter((v) => chiaveLavori(v.jobIds) !== chiave), s);
  const corrente = leggiCorrente(s); // anche se scaduto: va via comunque
  if (corrente && chiaveLavori(corrente.jobIds) === chiave) {
    try { storage(s)?.removeItem(CHIAVE_LAVORO); } catch (_) { /* noop */ }
  }
  avvisa(s);
}

/* ── Riapertura da un'altra pagina ──────────────────────────────────────────
 *
 * "Riapri" nella barra laterale dell'area clienti deve portare alla pagina di
 * anonimizzazione CON quel lavoro aperto; la stessa pagina, invece, aperta o
 * ricaricata per conto suo, parte sempre da un documento nuovo. La richiesta
 * sta nella memoria della scheda e si consuma una volta sola, cosi' un
 * aggiornamento successivo non la ritrova.
 */

export const CHIAVE_RIAPERTURA = "obscura_riapri";

function memoriaScheda(s) {
  if (s) return s;
  try { return window.sessionStorage; } catch (_) { return null; }
}

export function chiediRiapertura(voce, s) {
  const lavoro = normalizza(voce);
  if (!lavoro) return null;
  try { memoriaScheda(s)?.setItem(CHIAVE_RIAPERTURA, JSON.stringify(lavoro)); } catch (_) { /* niente memoria */ }
  return lavoro;
}

/** Il lavoro da riaprire, se qualcuno l'ha chiesto; poi la richiesta sparisce. */
export function prendiRiapertura(s) {
  try {
    const m = memoriaScheda(s);
    const raw = m?.getItem(CHIAVE_RIAPERTURA);
    if (!raw) return null;
    m.removeItem(CHIAVE_RIAPERTURA);
    return normalizza(JSON.parse(raw));
  } catch (_) {
    return null;
  }
}

/* ── Link dell'email "documenti pronti" (abbonati) ─────────────────────────
 *
 * `?page=anonymize&lavori=<id>,<id>&dal=<millisecondi>`: gli identificativi
 * dei lavori, che senza l'accesso all'account non aprono niente, e quando e'
 * partito l'invio, per dire fino a che ora i documenti restano disponibili.
 */

const ID_LAVORO = /^[A-Za-z0-9_-]{1,64}$/;
const MAX_LAVORI_LINK = 50;

/**
 * Il lavoro da riaprire descritto dal link, o null se il link non ne ha uno.
 * @param {URLSearchParams} params
 */
export function voceDaLinkEmail(params, adesso = Date.now()) {
  const ids = String(params?.get?.("lavori") || "")
    .split(",")
    .map((id) => id.trim())
    .filter((id) => ID_LAVORO.test(id))
    .slice(0, MAX_LAVORI_LINK);
  if (!ids.length) return null;
  const dal = Number(params.get("dal"));
  // Un "dal" nel futuro o assente non deve allungare la vita dichiarata dei documenti.
  const ts = Number.isFinite(dal) && dal > 0 && dal <= adesso ? dal : adesso;
  return normalizza({ jobIds: ids, sessionId: null, ts, nomi: [] });
}

/** Fino a che ora i documenti dovrebbero essere ancora disponibili. */
export function scadenzaLavoro(lavoro) {
  return lavoro && Number.isFinite(lavoro.ts) ? new Date(lavoro.ts + DURATA_LAVORO_MS) : null;
}

export function oraScadenza(lavoro) {
  const d = scadenzaLavoro(lavoro);
  return d ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "";
}
